import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import {
  createAccountDealRequest,
  deactivateClientRequest,
  exportClientsRequest,
  exportClientDetailRequest,
  exportPositionRequest,
  exportSummaryRequest,
  getClientAccountsRequest,
  getClientCashflowRequest,
  getClientDealsRequest,
  getClientPersonsRequest,
  getClientRequest,
  getClientRolesRequest,
  getClientsByParamRequest,
  getClientsRequest,
  getClientTaxPositionRequest,
  getClientTaxSummaryRequest,
  getPositionBuysRequest,
  postClientRequest,
  updateClientRequest,
  validClientRequest,
} from '../api/clientAPI'
import { ACTION_FAILED_ALERT_ID, CLIENT_INFORMATION_DIALOG_ID, DEPOSIT_DIALOG_ID } from '../constants/dialogConstants'
import {
  addClientAction,
  deactivateClientAction,
  getClientAccountsAction,
  getClientAction,
  getClientCashflowsAction,
  getClientCashflowsActionError,
  getClientDealsAction,
  getClientDealsActionError,
  getClientPersonsAction,
  getClientRolesAction,
  getClientsAction,
  getClientsActionAfter,
  getClientsActionBefore,
  getClientTaxPostitionAction,
  getClientTaxPostitionActionError,
  getClientTaxSummaryAction,
  getClientTaxSummaryActionError,
  getPositionBuysAction,
  updateClientAction,
  validClientAction,
} from '../redux/actions/clientActions'
import { closeDialog, openDialog } from '../redux/actions/dialogActions'
import { Client, ClientCashflow, ClientDeal, ClientRole, ClientTaxPosition, ClientTaxSummary } from '../redux/reducers/clients'
import { finishLoading, startLoading } from '../redux/actions/loadingActions'
import { lookup } from 'mime-types'
import { AccountDeal } from '../redux/reducers/portfolio'


export const useClientApi = () => {
  const dispatch = useDispatch()

  const getClients = useCallback(async () => {
    dispatch(getClientsActionBefore())

    await getClientsRequest()
      .then(response => {
        dispatch(getClientsAction(response.data))
      })
      .catch(error => {
        console.error(error)
      })
    dispatch(getClientsActionAfter())
  }, [])

  const postClient = useCallback(async (client: Client) => {
    await postClientRequest(client)
      .then(response => {
        dispatch(addClientAction(response.data))
        dispatch(closeDialog(CLIENT_INFORMATION_DIALOG_ID))
      })
      .catch(error => {
        console.error(error)
        dispatch(openDialog(ACTION_FAILED_ALERT_ID))
      })
  }, [])

  const updateClient = useCallback(async (client: Client) => {
    await updateClientRequest(client)
      .then(response => {
        dispatch(updateClientAction(response.data))
        dispatch(closeDialog(CLIENT_INFORMATION_DIALOG_ID))
      })
      .catch(error => {
        console.error(error)
        dispatch(openDialog(ACTION_FAILED_ALERT_ID))
      })
  }, [])

    const validClient = useCallback(async (client: Client) => {
    await validClientRequest(client)
      .then(response => {
        dispatch(validClientAction(response.data))
      })
      .catch(error => {
        console.error(error)
        dispatch(openDialog(ACTION_FAILED_ALERT_ID))
      })
  }, [])

  const deactivateClient = useCallback(async (client: Client) => {
    await deactivateClientRequest(client).then(response => {
      dispatch(deactivateClientAction(response.data))
    })
  }, [])

  const getClient = useCallback(async (clientId: number) => {
    dispatch(getClientsActionBefore())

    await getClientRequest(clientId)
      .then(response => {
        dispatch(getClientAction(response.data as Client))
      })
      .catch(error => {
        console.error(error)
      })

    dispatch(getClientsActionAfter())
  }, [])

  const getClientsByParam =
    useCallback(async (instrument: string, role: string) => {
      await getClientsByParamRequest(instrument, role)
        .then(response => {
          dispatch(getClientsAction(response.data as Client[]))
        })
        .catch(error => {
          console.error(error)
        })
    }, []) || []

  const getClientRoles = useCallback(async () => {
    await getClientRolesRequest()
      .then(response => {
        // BEZ_CILOVEHO_TRHU je oprávnění uživatele, ne obsluha klienta
        dispatch(getClientRolesAction(response.data.filter((role: ClientRole) => role.name !== 'BEZ_CILOVEHO_TRHU')))
      })
      .catch(error => {
        console.error(error)
      })
  }, [])

  const getClientPersons = useCallback(async (clientId: number) => {
    await getClientPersonsRequest(clientId)
      .then(response => {
        dispatch(getClientPersonsAction(response.data))
      })
      .catch(error => {
        console.error(error)
      })
  }, [])


  const getClientDeals = useCallback(async (clientId: number, dateFrom: String, dateTo: String) => {
    await getClientDealsRequest(clientId, dateFrom, dateTo)
      .then(response => {
        dispatch(getClientDealsAction(response.data as ClientDeal[]))
      })
      .catch(error => {
        console.error(error)
        dispatch(getClientDealsActionError())
      })
  }, [])

  const getClientCashflow = useCallback(async (clientId: number, dateFrom: String, dateTo: String) => {
    await getClientCashflowRequest(clientId, dateFrom, dateTo)
      .then(response => {
        dispatch(getClientCashflowsAction(response.data as ClientCashflow[]))
      })
      .catch(error => {
        console.error(error)
        dispatch(getClientCashflowsActionError())
      })
  }, [])

  const exportClients = useCallback(async (clientsId: number[]) => {
    dispatch(startLoading('exportFile'))
    await exportClientsRequest(clientsId)
      .then(async response => {
        dispatch(finishLoading('exportFile'))
        const link = document.createElement('a')
        const type = lookup(response.data.name)
        console.warn(type)
        link.href = `data:${type};base64,${response.data.content}`
        link.download = response.data.name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch(error => {
        dispatch(finishLoading('exportFile'))
        dispatch(openDialog(ACTION_FAILED_ALERT_ID))
        console.log(error)
      })
  }, [])

  const exportClientDetail = useCallback(async (clientId: number) => {
    dispatch(startLoading('exportFile'))
    await exportClientDetailRequest(clientId)
      .then(async response => {
        dispatch(finishLoading('exportFile'))
        const link = document.createElement('a')
        const type = lookup(response.data.name)
        link.href = `data:${type};base64,${response.data.content}`
        link.download = response.data.name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch(error => {
        dispatch(finishLoading('exportFile'))
        dispatch(openDialog(ACTION_FAILED_ALERT_ID))
        console.log(error)
      })
  }, [])

  const getClientTaxPosition = useCallback(async (clientId: number) => {
    await getClientTaxPositionRequest(clientId)
      .then(response => {
        dispatch(getClientTaxPostitionAction(response.data as ClientTaxPosition[]))
      })
      .catch(error => {
        console.error(error)
        dispatch(getClientTaxPostitionActionError())
      })
  }, [])

  const getClientTaxSummary = useCallback(async (clientId: number, dateFrom: String, dateTo: String) => {
    await getClientTaxSummaryRequest(clientId, dateFrom, dateTo)
      .then(response => {
        dispatch(getClientTaxSummaryAction(response.data as ClientTaxSummary[]))
      })
      .catch(error => {
        console.error(error)
        dispatch(getClientTaxSummaryActionError())
      })
  }, [])

  const exportSummary = useCallback(async (taxSummary: ClientTaxSummary, clientId: String, dateFrom: String, dateTo: String) => {
    dispatch(startLoading('exportFile'))
    await exportSummaryRequest(taxSummary, clientId, dateFrom, dateTo)
      .then(async response => {
        dispatch(finishLoading('exportFile'))
        const link = document.createElement('a')
        const type = lookup(response.data.name)
        console.warn(type)
        link.href = `data:${type};base64,${response.data.content}`
        link.download = response.data.name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch(error => {
        dispatch(finishLoading('exportFile'))
        dispatch(openDialog(ACTION_FAILED_ALERT_ID))
        console.log(error)
      })
  }, [])

  const exportPosition = useCallback(async (taxPosition: ClientTaxPosition, clientId: String, dateFrom: String, dateTo: String) => {
    dispatch(startLoading('exportFile'))
    await exportPositionRequest(taxPosition, clientId, dateFrom, dateTo)
      .then(async response => {
        dispatch(finishLoading('exportFile'))
        const link = document.createElement('a')
        const type = lookup(response.data.name)
        console.warn(type)
        link.href = `data:${type};base64,${response.data.content}`
        link.download = response.data.name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch(error => {
        dispatch(finishLoading('exportFile'))
        dispatch(openDialog(ACTION_FAILED_ALERT_ID))
        console.log(error)
      })
  }, [])

  const getPositionBuys = useCallback(async (id: Number) => {
    await getPositionBuysRequest(id)
      .then(response => {
        dispatch(getPositionBuysAction(id, response.data))
      })
      .catch(error => {
        console.error(error)
      })
  }, [])

  const getClientAccounts = useCallback(async (clientId: number) => {
    await getClientAccountsRequest(clientId)
      .then(response => {
        dispatch(getClientAccountsAction(response.data))
      })
      .catch(error => {
        console.error(error)
      })
  }, [])

  const createAccountDeal = useCallback(async (accountDeal: AccountDeal) => {
    await createAccountDealRequest(accountDeal)
      .then(response => {
        dispatch(closeDialog(DEPOSIT_DIALOG_ID))
        getClients()
      })
      .catch(error => {
        console.error(error)
      })
  }, [])


  return {
    getClients,
    getClient,
    postClient,
    updateClient,
    validClient,
    deactivateClient,
    getClientsByParam,
    getClientRoles,
    getClientPersons,
    getClientDeals,
    getClientCashflow,
    exportClients,
    exportClientDetail,
    getClientTaxPosition,
    getClientTaxSummary,
    exportSummary,
    exportPosition,
    getPositionBuys,
    getClientAccounts,
    createAccountDeal
  }
}
